import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const DURATIONS = [15, 20, 30, 45, 60];
const STATUS_TEXT = { proposed: "Waiting for the applicant's reply", accepted: "Accepted by the applicant", declined: "Applicant asked for another time" };

export const formatSlot = (iso) => {
  const d = new Date(iso);
  const base = new Intl.DateTimeFormat(undefined, { dateStyle: "full", timeStyle: "short" }).format(d);
  const tz = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value;
  return tz ? `${base} ${tz}` : base;
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const InviteSummary = ({ ci, id }) => (
  <div className="rounded-md bg-secondary p-3 text-sm" data-testid={`call-summary-${id}`}>
    <p className="font-semibold">{formatSlot(ci.slot_at)} · {ci.duration_min} min</p>
    <p className="mt-1 text-muted-foreground" data-testid={`call-status-${id}`}>{STATUS_TEXT[ci.status]}</p>
    {ci.response_note && <p className="mt-1">“{ci.response_note}”</p>}
  </div>
);

export const CallSlotPanel = ({ a, onChange }) => {
  const ci = a.call_invite;
  const [f, setF] = useState({ slot: toLocalInput(ci?.slot_at), duration_min: ci?.duration_min || 20, link: ci?.link || "", note: ci?.note || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const send = () => {
    if (!f.slot) { toast.error("Choose a date and time"); return; }
    run(() => api.put(`/admin/applications/${a.id}/call`, { slot_at: new Date(f.slot).toISOString(), duration_min: Number(f.duration_min), link: f.link, note: f.note }), "Call time sent to the applicant");
  };
  return (
    <div className="space-y-3 rounded-lg border border-border p-4" data-testid={`call-panel-${a.id}`}>
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary"><CalendarClock className="h-4 w-4" /> Send a one-off call time</p>
      {ci && <InviteSummary ci={ci} id={a.id} />}
      <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
        <input type="datetime-local" className="field" value={f.slot} onChange={set("slot")} aria-label="Call date and time (your local time)" data-testid={`call-slot-input-${a.id}`} />
        <select className="field" value={f.duration_min} onChange={set("duration_min")} aria-label="Call length" data-testid={`call-duration-${a.id}`}>
          {DURATIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
        </select>
      </div>
      <input className="field" type="url" maxLength={300} placeholder="Call link, e.g. https://meet.google.com/… (optional)" value={f.link} onChange={set("link")} aria-label="Call link" data-testid={`call-link-${a.id}`} />
      <input className="field" maxLength={500} placeholder="Message to the applicant (optional)" value={f.note} onChange={set("note")} aria-label="Message to the applicant" data-testid={`call-note-${a.id}`} />
      <p className="text-xs text-muted-foreground">Times use your local timezone. We'll email the applicant, and they see the time in their own timezone when they sign in.</p>
      <div className="flex flex-wrap gap-3">
        <button type="button" className="btn-primary" disabled={busy} onClick={send} data-testid={`call-send-${a.id}`}>{ci ? "Send new time" : "Send call time"}</button>
        {ci && <button type="button" className="btn-outline" disabled={busy} onClick={() => run(() => api.delete(`/admin/applications/${a.id}/call`), "Call cancelled")} data-testid={`call-cancel-${a.id}`}>Cancel call</button>}
      </div>
    </div>
  );
};
